import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { IconX } from '@tabler/icons-react'
import { supabase } from '../../lib/supabase'
import { getBilling } from '../../lib/api'
import { useBillingPlan } from '../../lib/useBillingPlan'
import { useI18n } from '../../lib/i18n'
import { UpsellCard } from './FeatureUpsell'
import { canInvite, invitationKey, invitationSessionKey, readInvitation, INVITATION_DELAY_MS, INVITATION_IDLE_MS } from './planInvitationPolicy'

/** Se monta fuera del menú; sólo invita en una pausa del Resumen. */
export function PlanInvitation({ userId, eligible, onOpenPlans }: { userId: string; eligible: boolean; onOpenPlans: () => void }) {
  const { t } = useI18n()
  const { plan, confirmed } = useBillingPlan()
  const [open, setOpen] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    if (!confirmed || plan !== 'free') return
    try {
      const history = readInvitation(userId, Date.now())
      if (!window.sessionStorage.getItem(invitationSessionKey(userId))) {
        history.sessions += 1
        window.sessionStorage.setItem(invitationSessionKey(userId), 'visited')
      }
      window.localStorage.setItem(invitationKey(userId), JSON.stringify(history))
    } catch { /* Sin almacenamiento fiable no se muestran promociones automáticas. */ }
  }, [userId, confirmed, plan])

  useEffect(() => {
    if (!eligible || !confirmed || plan !== 'free') return
    let alive = true
    let checking = false
    let suppressed = false
    let startedAt = Date.now()
    let lastActivity = startedAt
    let interacted = false
    const activity = () => { interacted = true; lastActivity = Date.now() }
    const visibilityChanged = () => {
      startedAt = Date.now()
      lastActivity = startedAt
      interacted = false
    }
    const blocked = () => {
      const active = document.activeElement
      return document.visibilityState !== 'visible'
        || Boolean(document.querySelector('[aria-modal="true"], dialog[open], .dash-sidebar-backdrop.is-open'))
        || (active instanceof HTMLElement && Boolean(active.closest('input, textarea, select, [contenteditable="true"]')))
    }
    const tick = async () => {
      if (document.visibilityState !== 'visible') { startedAt = Date.now(); return }
      if (suppressed || checking || !interacted || Date.now() - startedAt < INVITATION_DELAY_MS || Date.now() - lastActivity < INVITATION_IDLE_MS || blocked()) return
      try {
        if (window.sessionStorage.getItem(invitationSessionKey(userId)) === 'shown' || !canInvite(readInvitation(userId, Date.now()), Date.now())) return
        checking = true
        // Reconfirmar contra el servidor: una compra en otra pestaña no deja
        // un plan gratis obsoleto habilitando esta promoción.
        const { data } = await supabase.auth.getSession()
        if (!data.session || data.session.user.id !== userId) { suppressed = true; return }
        const billing = await getBilling(data.session.access_token)
        if (billing.plan !== 'free') { suppressed = true; return }
        if (!alive || billing.plan !== 'free' || blocked() || Date.now() - lastActivity < INVITATION_IDLE_MS) return
        const history = readInvitation(userId, Date.now())
        if (!canInvite(history, Date.now()) || window.sessionStorage.getItem(invitationSessionKey(userId)) === 'shown') return
        history.impressions += 1
        history.lastShownAt = Date.now()
        window.localStorage.setItem(invitationKey(userId), JSON.stringify(history))
        window.sessionStorage.setItem(invitationSessionKey(userId), 'shown')
        window.clearInterval(timer)
        setOpen(true)
      } catch { suppressed = true /* Fallo de red o almacenamiento: no interrumpir ni insistir. */ }
      finally { checking = false }
    }
    window.addEventListener('pointerdown', activity, { passive: true })
    window.addEventListener('keydown', activity)
    window.addEventListener('scroll', activity, { passive: true, capture: true })
    document.addEventListener('visibilitychange', visibilityChanged)
    const timer = window.setInterval(() => { void tick() }, 1000)
    return () => {
      alive = false
      window.clearInterval(timer)
      window.removeEventListener('pointerdown', activity)
      window.removeEventListener('keydown', activity)
      window.removeEventListener('scroll', activity, true)
      document.removeEventListener('visibilitychange', visibilityChanged)
    }
  }, [userId, eligible, confirmed, plan])

  useEffect(() => {
    if (!eligible || !confirmed || plan !== 'free') setOpen(false)
  }, [eligible, confirmed, plan])

  const visible = open && eligible && confirmed && plan === 'free'
  useEffect(() => {
    if (!visible || !dialogRef.current) return
    const dialog = dialogRef.current
    const focus = document.activeElement
    const overflow = document.body.style.overflow
    dialog.showModal()
    document.body.style.overflow = 'hidden'
    return () => {
      dialog.close()
      document.body.style.overflow = overflow
      if (focus instanceof HTMLElement && focus.isConnected) focus.focus({ preventScroll: true })
    }
  }, [visible])

  const close = () => setOpen(false)
  const never = () => {
    try {
      const history = readInvitation(userId, Date.now())
      window.localStorage.setItem(invitationKey(userId), JSON.stringify({ ...history, never: true }))
    } catch { /* Ya está silenciada en esta sesión. */ }
    close()
  }
  if (!visible) return null
  return createPortal(
    <dialog ref={dialogRef} className="dash-plan-invitation" aria-modal="true" aria-label={t('sidebar.upgrade.freeTitle')} onCancel={(event) => { event.preventDefault(); close() }} onClick={(event) => { if (event.target === event.currentTarget) close() }}>
      <div className="dash-plan-invitation-inner">
        <header className="dash-plan-invitation-head"><button type="button" aria-label={t('common.close')} onClick={close}><IconX size={20} /></button></header>
        <UpsellCard standalone title={t('sidebar.upgrade.freeTitle')} description={t('sidebar.upgrade.freeBody')}
          points={['planInvitation.domain', 'planInvitation.pages', 'planInvitation.branding'].map((key) => t(key))}
          onOpenPlans={() => { close(); onOpenPlans() }} />
        <footer className="dash-plan-invitation-foot">
          <button type="button" onClick={close}>{t('planInvitation.notNow')}</button>
          <button type="button" onClick={never}>{t('upsell.dismiss')}</button>
        </footer>
      </div>
    </dialog>, document.body,
  )
}
